class ProductDetailVideoStory extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.video = this.querySelector('[data-video-story-media]');
    this.control = this.querySelector('[data-video-story-control]');
    if (!this.video || !this.control) return;

    this.soundControl = this.querySelector('[data-video-story-sound-control]');

    this.initialized = true;
    this.sourcesReady = this.video.querySelector('source[src]') !== null;
    this.isIntersecting = false;
    this.isManuallyPaused = false;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handleControlClick = this.handleControlClick.bind(this);
    this.handleSoundControlClick = this.handleSoundControlClick.bind(this);
    this.handleLoadIntersection = this.handleLoadIntersection.bind(this);
    this.handlePlaybackIntersection = this.handlePlaybackIntersection.bind(this);
    this.handleMotionChange = this.handleMotionChange.bind(this);
    this.handlePlaying = this.handlePlaying.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.handleError = this.handleError.bind(this);

    this.control.addEventListener('click', this.handleControlClick);
    this.soundControl?.addEventListener('click', this.handleSoundControlClick);
    this.video.addEventListener('playing', this.handlePlaying);
    this.video.addEventListener('pause', this.handlePause);
    this.video.addEventListener('ended', this.handlePause);
    this.video.addEventListener('error', this.handleError);
    this.motionQuery.addEventListener('change', this.handleMotionChange);
    this.updateSoundState();

    if ('IntersectionObserver' in window) {
      this.loadObserver = new IntersectionObserver(this.handleLoadIntersection, {
        rootMargin: '200px 0px',
        threshold: 0,
      });
      this.playbackObserver = new IntersectionObserver(this.handlePlaybackIntersection, {
        threshold: 0.1,
      });
      this.loadObserver.observe(this);
      this.playbackObserver.observe(this);
      return;
    }

    this.hydrateVideo();
    this.isIntersecting = true;
    this.syncPlayback();
  }

  hydrateVideo() {
    if (this.sourcesReady) return;

    const sources = this.video.querySelectorAll('source[data-src]');
    if (!sources.length) return;

    sources.forEach((source) => {
      source.src = source.dataset.src;
    });
    this.sourcesReady = true;
    this.video.load();
  }

  handleLoadIntersection(entries) {
    const entry = entries.find(({ target }) => target === this);
    if (!entry?.isIntersecting) return;

    this.hydrateVideo();
    this.loadObserver?.disconnect();
    this.loadObserver = null;
  }

  handlePlaybackIntersection(entries) {
    const entry = entries.find(({ target }) => target === this);
    if (!entry) return;

    this.isIntersecting = entry.isIntersecting;
    this.syncPlayback();
  }

  handleControlClick() {
    if (this.video.paused) {
      this.isManuallyPaused = false;
      this.hydrateVideo();
      this.playVideo();
      return;
    }

    this.isManuallyPaused = true;
    this.video.pause();
  }

  handleSoundControlClick() {
    this.video.muted = !this.video.muted;
    this.updateSoundState();
  }

  handleMotionChange(event) {
    if (event.matches) {
      this.video.pause();
      return;
    }

    this.syncPlayback();
  }

  syncPlayback() {
    if (!this.isIntersecting || this.isManuallyPaused || this.motionQuery.matches) {
      this.video.pause();
      return;
    }

    this.hydrateVideo();
    this.playVideo();
  }

  playVideo() {
    this.video.play().catch(() => this.updatePlaybackState(false));
  }

  handlePlaying() {
    this.classList.add('has-started');
    this.updatePlaybackState(true);
  }

  handlePause() {
    this.updatePlaybackState(false);
  }

  handleError() {
    this.classList.remove('has-started');
    this.updatePlaybackState(false);
  }

  updatePlaybackState(isPlaying) {
    const label = isPlaying ? this.control.dataset.pauseLabel : this.control.dataset.playLabel;
    this.classList.toggle('is-playing', isPlaying);
    this.control.setAttribute('aria-pressed', String(isPlaying));
    this.control.setAttribute('aria-label', label);
  }

  updateSoundState() {
    if (!this.soundControl) return;

    const isMuted = this.video.muted;
    const label = isMuted
      ? this.soundControl.dataset.mutedLabel
      : this.soundControl.dataset.unmutedLabel;

    this.classList.toggle('is-muted', isMuted);
    this.soundControl.setAttribute('aria-pressed', String(!isMuted));
    this.soundControl.setAttribute('aria-label', label);
  }

  disconnectedCallback() {
    this.loadObserver?.disconnect();
    this.playbackObserver?.disconnect();
    this.control?.removeEventListener('click', this.handleControlClick);
    this.soundControl?.removeEventListener('click', this.handleSoundControlClick);
    this.video?.removeEventListener('playing', this.handlePlaying);
    this.video?.removeEventListener('pause', this.handlePause);
    this.video?.removeEventListener('ended', this.handlePause);
    this.video?.removeEventListener('error', this.handleError);
    this.motionQuery?.removeEventListener('change', this.handleMotionChange);
    this.video?.pause();
    this.initialized = false;
  }
}

if (!customElements.get('product-detail-video-story')) {
  customElements.define('product-detail-video-story', ProductDetailVideoStory);
}
